import {
  TTORIA_ASYNC_FEEDBACK_OP_CLASSES,
  TTORIA_ASYNC_FEEDBACK_OP_PRIORITY
} from '../../ttoria-async-feedback/src/contract.js';

const OP_CLASS_SET = new Set(TTORIA_ASYNC_FEEDBACK_OP_CLASSES);
const SELECTOR_PRESENTERS = new Set(['control', 'region', 'rail']);
const TERMINAL_STATES = new Set(['succeeded', 'failed', 'cancelled', 'stale-discarded']);
const CATALOG_ALIAS_KINDS = new Set(['interaction', 'interaction-prefix', 'form']);
const SAFE_ID = /^[a-z0-9][a-z0-9._:-]{0,199}$/iu;

function cleanText(value = '', maxLength = 320) {
  return String(value ?? '')
    .replace(/[\u0000-\u0008\u000b\u000c\u000e-\u001f\u007f]/gu, '')
    .replace(/\s+/gu, ' ')
    .trim()
    .slice(0, maxLength);
}

function isPlainRecord(value) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const prototype = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
}

function assertRecord(value, label) {
  if (!isPlainRecord(value)) throw new TypeError(`${label} must be a plain object.`);
}

function assertOnlyKeys(value, allowed, label) {
  const extras = Object.keys(value).filter((key) => !allowed.has(key));
  if (extras.length > 0) {
    throw new TypeError(`${label} contains prohibited field(s): ${extras.join(', ')}.`);
  }
}

function immutable(value) {
  if (!value || typeof value !== 'object' || Object.isFrozen(value)) return value;
  for (const child of Object.values(value)) immutable(child);
  return Object.freeze(value);
}

function normalizeStableId(value = '', label = 'id') {
  const normalized = cleanText(value, 200);
  if (!SAFE_ID.test(normalized) || /^[.#\[]/u.test(normalized)) {
    throw new TypeError(`${label} must be a stable local identifier, not a DOM node or selector.`);
  }
  return normalized;
}

function normalizeTarget(raw = null, fallback = null) {
  const candidate = raw == null ? fallback : raw;
  if (candidate == null) return null;
  assertRecord(candidate, 'activity target');
  assertOnlyKeys(candidate, new Set(['kind', 'id']), 'activity target');
  const kind = cleanText(candidate.kind, 20);
  if (kind !== 'control' && kind !== 'region') {
    throw new TypeError('activity target.kind must be control or region.');
  }
  return immutable({ kind, id: normalizeStableId(candidate.id, 'activity target.id') });
}

function normalizeProgress(raw) {
  if (raw == null) return null;
  assertRecord(raw, 'activity progress');
  assertOnlyKeys(raw, new Set(['value', 'max']), 'activity progress');
  const value = Number(raw.value);
  const max = Number(raw.max);
  if (!Number.isFinite(value) || !Number.isFinite(max) || max <= 0 || value < 0 || value > max) {
    throw new TypeError('activity progress must contain measured numeric {value,max} with 0 <= value <= max.');
  }
  return immutable({ value, max });
}

function normalizeSelector(raw) {
  assertRecord(raw, 'activity selector');
  assertOnlyKeys(raw, new Set(['presenter', 'id']), 'activity selector');
  const presenter = cleanText(raw.presenter, 20);
  if (!SELECTOR_PRESENTERS.has(presenter)) {
    throw new TypeError('activity selector.presenter must be control, region, or rail.');
  }
  if (presenter === 'rail') {
    if (Object.prototype.hasOwnProperty.call(raw, 'id')) {
      throw new TypeError('rail selectors must not contain an id.');
    }
    return immutable({ presenter: 'rail' });
  }
  return immutable({ presenter, id: normalizeStableId(raw.id, 'activity selector.id') });
}

function normalizeCatalog(raw = []) {
  const rows = Array.isArray(raw) ? raw : Array.isArray(raw?.operations) ? raw.operations : [];
  const byAction = new Map();
  for (const row of rows) {
    if (!isPlainRecord(row)) continue;
    const action = cleanText(row.action || row.operationId, 200);
    const opClass = cleanText(row.opClass || row.classification?.opClass, 40);
    if (!SAFE_ID.test(action) || !OP_CLASS_SET.has(opClass) || row.lifecycleRequired === false) continue;
    if (byAction.has(action)) throw new Error(`Duplicate async activity catalog action: ${action}`);
    byAction.set(action, immutable({
      action,
      kind: cleanText(row.kind, 40),
      opClass,
      target: normalizeTarget(row.target || row.classification?.target || null),
      duplicatePolicy: cleanText(row.duplicatePolicy || row.classification?.duplicatePolicy, 40) || 'latest-result-wins',
      retryPolicy: cleanText(row.retryPolicy || row.classification?.retryPolicy, 40) || 'reconcile-only',
      cancellationPolicy: cleanText(row.cancellationPolicy || row.classification?.cancellationPolicy, 40) || 'not-applicable',
      dataPolicy: cleanText(row.dataPolicy || row.classification?.dataPolicy, 40) || 'retain-valid-content',
      successDestination: cleanText(row.successDestination || row.classification?.successDestination, 60) || 'affected-region',
      announcementOwner: cleanText(row.announcementOwner || row.classification?.announcementOwner, 120)
    }));
  }
  return byAction;
}

function buildCatalogAliases(catalogByAction = new Map()) {
  const candidates = new Map();
  for (const entry of catalogByAction.values()) {
    if (!CATALOG_ALIAS_KINDS.has(entry.kind) || !entry.target?.id) continue;
    const alias = entry.target.id;
    if (!candidates.has(alias)) candidates.set(alias, []);
    candidates.get(alias).push(entry);
  }
  return new Map(
    [...candidates.entries()]
      .filter(([, entries]) => entries.length === 1)
      .map(([alias, entries]) => [alias, entries[0]])
  );
}

function selectorKey(selector) {
  return selector.presenter === 'rail' ? 'rail' : `${selector.presenter}:${selector.id}`;
}

function activityIdentity(activity) {
  const target = activity.target ? `${activity.target.kind}:${activity.target.id}` : 'rail';
  return `${activity.scopeKey}|${activity.action}|${target}`;
}

function compareActivities(left, right) {
  const priority = Number(TTORIA_ASYNC_FEEDBACK_OP_PRIORITY[left.opClass] || 0)
    - Number(TTORIA_ASYNC_FEEDBACK_OP_PRIORITY[right.opClass] || 0);
  if (priority !== 0) return priority;
  return Number(left.sequence || 0) - Number(right.sequence || 0);
}

function publicActivity(activity) {
  return immutable({
    id: activity.id,
    action: activity.action,
    opClass: activity.opClass,
    state: activity.state,
    summary: activity.summary,
    detail: activity.detail,
    target: activity.target ? { ...activity.target } : null,
    progress: activity.progress ? { ...activity.progress } : null,
    silent: activity.silent,
    appId: activity.appId,
    pluginId: activity.pluginId,
    routeKey: activity.routeKey,
    routeEpoch: activity.routeEpoch,
    mountId: activity.mountId,
    sequence: activity.sequence,
    startedAt: activity.startedAt,
    updatedAt: activity.updatedAt,
    settledAt: activity.settledAt,
    cancelReason: activity.cancelReason || '',
    retryPolicy: activity.retryPolicy,
    cancellationPolicy: activity.cancellationPolicy,
    dataPolicy: activity.dataPolicy,
    successDestination: activity.successDestination,
    announcementOwner: activity.announcementOwner,
    duplicateSuppressed: activity.duplicateSuppressed === true
  });
}

export function createTtoriaActivityRegistry({
  appId = '',
  catalog = [],
  now = () => Date.now(),
  randomId = null,
  onTerminal = null
} = {}) {
  const normalizedAppId = normalizeStableId(appId, 'activity appId');
  const catalogByAction = normalizeCatalog(catalog);
  const catalogByAlias = buildCatalogAliases(catalogByAction);
  const activities = new Map();
  const handles = new Map();
  const scopes = new Map();
  const latestByIdentity = new Map();
  let sequence = 0;
  let idSequence = 0;

  function nextId() {
    idSequence += 1;
    const suffix = typeof randomId === 'function'
      ? cleanText(randomId(), 80)
      : globalThis.crypto && typeof globalThis.crypto.randomUUID === 'function'
        ? globalThis.crypto.randomUUID().replace(/-/gu, '')
        : `${Math.max(0, Number(now()) || 0).toString(36)}${idSequence.toString(36)}`;
    return `act_${idSequence.toString(36)}_${suffix.replace(/[^a-z0-9]/giu, '').slice(0, 64) || 'local'}`;
  }

  function scopeActivities(scope) {
    return Array.from(activities.values()).filter((activity) => activity.scopeKey === scope.key);
  }

  function buildSnapshot(scope, rawSelector) {
    const selector = normalizeSelector(rawSelector);
    const rows = scopeActivities(scope)
      .filter((activity) => {
        const presentationCurrent = activity.state !== 'pending'
          || activity.catalogEntry.duplicatePolicy !== 'latest-result-wins'
          || latestByIdentity.get(activityIdentity(activity)) === activity.id;
        if (!presentationCurrent) return false;
        if (selector.presenter === 'rail') {
          return activity.state === 'pending' && !activity.silent;
        }
        return activity.target?.kind === selector.presenter
          && activity.target?.id === selector.id
          && activity.state !== 'stale-discarded';
      })
      .sort(compareActivities)
      .map(publicActivity);
    return immutable({
      selector: { ...selector },
      appId: scope.appId,
      pluginId: scope.pluginId,
      routeKey: scope.routeKey,
      routeEpoch: scope.routeEpoch,
      mountId: scope.mountId,
      activities: rows,
      pendingCount: rows.filter((row) => row.state === 'pending').length,
      failedCount: rows.filter((row) => row.state === 'failed').length,
      winningActivity: selector.presenter === 'rail' ? rows.at(-1) || null : null
    });
  }

  function notifyScope(scope) {
    for (const subscription of scope.subscriptions.values()) {
      try {
        subscription.listener(buildSnapshot(scope, subscription.selector));
      } catch (error) {
        console.warn('[ttoria-activity] subscriber failed', error);
      }
    }
  }

  function emitTerminal(activity, staleDiscarded = false) {
    if (typeof onTerminal !== 'function') return;
    try {
      onTerminal(immutable({
        activity: publicActivity(activity),
        staleDiscarded,
        durationMs: Math.max(0, Number(activity.settledAt) - Number(activity.startedAt))
      }));
    } catch {
      console.warn('[ttoria-activity] terminal observer failed');
    }
  }

  function settle(activity, state, payload = {}) {
    if (!activity || activity.state !== 'pending' || !activity.scope.active) return false;
    if (activity.abortSignal && activity.abortListener) {
      activity.abortSignal.removeEventListener('abort', activity.abortListener);
      activity.abortSignal = null;
      activity.abortListener = null;
    }
    const catalogEntry = activity.catalogEntry;
    const identity = activityIdentity(activity);
    if (
      catalogEntry.duplicatePolicy === 'latest-result-wins'
      && latestByIdentity.get(identity) !== activity.id
    ) {
      activity.state = 'stale-discarded';
      activity.settledAt = Number(now());
      activity.updatedAt = activity.settledAt;
      notifyScope(activity.scope);
      emitTerminal(activity, true);
      activities.delete(activity.id);
      return false;
    }
    activity.state = state;
    activity.summary = cleanText(payload.summary || activity.summary, 240) || activity.summary;
    activity.detail = cleanText(payload.detail || '', 480);
    activity.cancelReason = state === 'cancelled' ? cleanText(payload.reason || '', 120) : '';
    activity.settledAt = Number(now());
    activity.updatedAt = activity.settledAt;
    if (state === 'cancelled') {
      activities.delete(activity.id);
      if (latestByIdentity.get(identity) === activity.id) latestByIdentity.delete(identity);
    }
    notifyScope(activity.scope);
    emitTerminal(activity, false);
    return true;
  }

  function makeHandle(activity) {
    const handle = Object.freeze({
      id: activity.id,
      update(payload = {}) {
        if (!activity.scope.active || activity.state !== 'pending') return false;
        assertRecord(payload, 'activity update');
        assertOnlyKeys(payload, new Set(['summary', 'detail', 'progress']), 'activity update');
        if (Object.prototype.hasOwnProperty.call(payload, 'summary')) {
          const summary = cleanText(payload.summary, 240);
          if (!summary) throw new TypeError('activity update.summary cannot be empty.');
          activity.summary = summary;
        }
        if (Object.prototype.hasOwnProperty.call(payload, 'detail')) {
          activity.detail = cleanText(payload.detail, 480);
        }
        if (Object.prototype.hasOwnProperty.call(payload, 'progress')) {
          activity.progress = normalizeProgress(payload.progress);
        }
        activity.updatedAt = Number(now());
        notifyScope(activity.scope);
        return true;
      },
      succeed(payload = {}) {
        assertRecord(payload, 'activity success');
        assertOnlyKeys(payload, new Set(['summary', 'detail']), 'activity success');
        return settle(activity, 'succeeded', payload);
      },
      fail(payload = {}) {
        assertRecord(payload, 'activity failure');
        assertOnlyKeys(payload, new Set(['summary', 'detail']), 'activity failure');
        const summary = cleanText(payload.summary, 240);
        if (!summary) throw new TypeError('activity failure.summary is required.');
        return settle(activity, 'failed', { ...payload, summary });
      },
      cancel(payload = {}) {
        assertRecord(payload, 'activity cancellation');
        assertOnlyKeys(payload, new Set(['reason']), 'activity cancellation');
        return settle(activity, 'cancelled', payload);
      }
    });
    handles.set(activity.id, handle);
    return handle;
  }

  function begin(scope, payload = {}, { legacy = false } = {}) {
    if (!scope.active) throw new Error('Cannot begin activity in an invalidated route or mount scope.');
    assertRecord(payload, 'activity begin');
    const allowedKeys = legacy
      ? new Set(['action', 'opClass', 'summary', 'detail', 'target', 'silent'])
      : new Set(['action', 'opClass', 'summary', 'detail', 'target', 'silent', 'signal']);
    assertOnlyKeys(payload, allowedKeys, 'activity begin');
    const requestedAction = legacy
      ? normalizeStableId(payload.action, 'legacy activity action')
      : normalizeStableId(payload.action, 'activity action');
    const catalogEntry = legacy
      ? immutable({
        action: requestedAction,
        opClass: 'legacy',
        target: normalizeTarget(payload.target || null),
        duplicatePolicy: 'latest-result-wins',
        retryPolicy: 'reconcile-only',
        cancellationPolicy: 'not-applicable',
        dataPolicy: 'retain-valid-content',
        successDestination: 'affected-region',
        announcementOwner: `${scope.pluginId}-legacy-status`
      })
      : catalogByAction.get(requestedAction) || catalogByAlias.get(requestedAction);
    if (!catalogEntry) throw new Error(`Uncatalogued async activity action: ${requestedAction}`);
    const action = catalogEntry.action;
    const opClass = cleanText(payload.opClass, 40);
    if (legacy ? opClass !== 'legacy' : !OP_CLASS_SET.has(opClass)) {
      throw new TypeError('activity opClass must be route-transition, user-mutation, foreground-read, or background-refresh.');
    }
    if (!legacy && opClass === 'legacy') throw new TypeError('legacy is adapter-only.');
    if (!legacy && catalogEntry.opClass !== opClass) {
      throw new Error(`Activity opClass does not match catalog action ${action}.`);
    }
    const summary = cleanText(payload.summary, 240);
    if (!summary) throw new TypeError('activity begin.summary is required.');
    if (Object.prototype.hasOwnProperty.call(payload, 'silent') && typeof payload.silent !== 'boolean') {
      throw new TypeError('activity begin.silent must be boolean when provided.');
    }
    const signal = legacy ? null : payload.signal;
    if (
      signal != null
      && (
        typeof signal !== 'object'
        || typeof signal.addEventListener !== 'function'
        || typeof signal.removeEventListener !== 'function'
        || typeof signal.aborted !== 'boolean'
      )
    ) {
      throw new TypeError('activity begin.signal must be an AbortSignal.');
    }
    const target = normalizeTarget(payload.target, catalogEntry.target);
    const startedAt = Number(now());
    const activity = {
      id: nextId(),
      action,
      opClass,
      state: 'pending',
      summary,
      detail: cleanText(payload.detail, 480),
      target,
      progress: null,
      silent: payload.silent === true,
      appId: scope.appId,
      pluginId: scope.pluginId,
      routeKey: scope.routeKey,
      routeEpoch: scope.routeEpoch,
      mountId: scope.mountId,
      scope,
      scopeKey: scope.key,
      sequence: ++sequence,
      startedAt,
      updatedAt: startedAt,
      settledAt: null,
      cancelReason: '',
      retryPolicy: catalogEntry.retryPolicy,
      cancellationPolicy: catalogEntry.cancellationPolicy,
      dataPolicy: catalogEntry.dataPolicy,
      successDestination: catalogEntry.successDestination,
      announcementOwner: catalogEntry.announcementOwner,
      duplicateSuppressed: false,
      catalogEntry,
      abortSignal: null,
      abortListener: null
    };
    const identity = activityIdentity(activity);
    if (catalogEntry.duplicatePolicy === 'suppress-while-pending') {
      const existing = Array.from(activities.values()).find((candidate) => (
        candidate.state === 'pending' && activityIdentity(candidate) === identity
      ));
      if (existing) {
        existing.duplicateSuppressed = true;
        existing.updatedAt = Number(now());
        notifyScope(existing.scope);
        return handles.get(existing.id);
      }
    }
    for (const candidate of Array.from(activities.values())) {
      if (activityIdentity(candidate) !== identity || !TERMINAL_STATES.has(candidate.state)) continue;
      activities.delete(candidate.id);
      handles.delete(candidate.id);
    }
    latestByIdentity.set(identity, activity.id);
    activities.set(activity.id, activity);
    const handle = makeHandle(activity);
    if (signal != null) {
      const abortListener = () => handle.cancel({ reason: 'presentation-aborted' });
      activity.abortSignal = signal;
      activity.abortListener = abortListener;
      signal.addEventListener('abort', abortListener, { once: true });
      if (signal.aborted) abortListener();
    }
    notifyScope(scope);
    return handle;
  }

  function createLegacyAdapter(scope) {
    const byOpKey = new Map();
    return Object.freeze({
      intent(detail = {}) {
        const raw = isPlainRecord(detail) ? detail : {};
        const opKey = cleanText(raw.opKey || `${raw.opClass || 'status'}:${raw.routeKey || scope.routeKey}`, 160);
        const action = `legacy:${opKey || 'status'}`
          .toLowerCase()
          .replace(/[^a-z0-9._:-]+/gu, '-')
          .replace(/^-+|-+$/gu, '')
          .slice(0, 200);
        const existing = byOpKey.get(opKey);
        const summary = cleanText(raw.summary || raw.message || 'Working', 240) || 'Working';
        const detailText = raw.tone === 'err' ? '' : cleanText(raw.detail || '', 480);
        if (raw.busy === true) {
          if (existing) {
            existing.update({ summary, detail: detailText });
            return existing;
          }
          const handle = begin(scope, {
            action,
            opClass: 'legacy',
            summary,
            detail: detailText,
            target: { kind: 'region', id: normalizeStableId(raw.routeKey || scope.routeKey, 'legacy route key') },
            silent: false
          }, { legacy: true });
          byOpKey.set(opKey, handle);
          return handle;
        }
        if (!existing) return null;
        if (raw.tone === 'err') existing.fail({ summary, detail: '' });
        else existing.succeed({ summary, detail: detailText });
        byOpKey.delete(opKey);
        return existing;
      },
      clear(detail = {}) {
        const raw = isPlainRecord(detail) ? detail : {};
        const requestedOpKey = cleanText(raw.opKey, 160);
        let removed = false;
        for (const [opKey, handle] of byOpKey.entries()) {
          if (requestedOpKey && requestedOpKey !== opKey) continue;
          handle.cancel({ reason: 'legacy-status-cleared' });
          byOpKey.delete(opKey);
          removed = true;
        }
        return removed;
      }
    });
  }

  function createScope({ pluginId = '', routeKey = '', routeEpoch = 0, mountId = '' } = {}) {
    const normalizedPluginId = normalizeStableId(pluginId, 'activity pluginId');
    const normalizedRouteKey = normalizeStableId(routeKey, 'activity routeKey');
    const normalizedMountId = normalizeStableId(mountId, 'activity mountId');
    const normalizedRouteEpoch = Number(routeEpoch);
    if (!Number.isSafeInteger(normalizedRouteEpoch) || normalizedRouteEpoch < 0) {
      throw new TypeError('activity routeEpoch must be a non-negative safe integer.');
    }
    const key = `${normalizedAppId}|${normalizedPluginId}|${normalizedRouteKey}|${normalizedRouteEpoch}|${normalizedMountId}`;
    if (scopes.has(key)) throw new Error(`Activity scope already exists: ${key}`);
    const scope = {
      key,
      appId: normalizedAppId,
      pluginId: normalizedPluginId,
      routeKey: normalizedRouteKey,
      routeEpoch: normalizedRouteEpoch,
      mountId: normalizedMountId,
      active: true,
      subscriptions: new Map(),
      subscriptionSequence: 0
    };
    scopes.set(key, scope);
    const legacyAdapter = createLegacyAdapter(scope);
    const publicScope = Object.freeze({
      begin(payload = {}) {
        return begin(scope, payload);
      },
      snapshot(selector) {
        return buildSnapshot(scope, selector);
      },
      subscribe(selector, listener) {
        if (typeof listener !== 'function') throw new TypeError('activity subscription listener must be a function.');
        const normalized = normalizeSelector(selector);
        const id = ++scope.subscriptionSequence;
        scope.subscriptions.set(id, { selector: normalized, listener });
        listener(buildSnapshot(scope, normalized));
        return () => scope.subscriptions.delete(id);
      },
      invalidate() {
        if (!scope.active) return false;
        scope.active = false;
        for (const activity of scopeActivities(scope)) {
          if (activity.abortSignal && activity.abortListener) {
            activity.abortSignal.removeEventListener('abort', activity.abortListener);
          }
          if (activity.state === 'pending') {
            activity.state = 'cancelled';
            activity.cancelReason = 'scope-invalidated';
            activity.settledAt = Number(now());
            activity.updatedAt = activity.settledAt;
            emitTerminal(activity, false);
          }
          activities.delete(activity.id);
          handles.delete(activity.id);
          if (latestByIdentity.get(activityIdentity(activity)) === activity.id) {
            latestByIdentity.delete(activityIdentity(activity));
          }
        }
        notifyScope(scope);
        scope.subscriptions.clear();
        scopes.delete(key);
        return true;
      },
      legacyAdapter
    });
    return publicScope;
  }

  return Object.freeze({
    createScope,
    catalogSize: catalogByAction.size,
    debugSnapshot() {
      return immutable({
        appId: normalizedAppId,
        scopeCount: scopes.size,
        activityCount: activities.size,
        activities: Array.from(activities.values()).map(publicActivity)
      });
    }
  });
}

export const TTORIA_ACTIVITY_TERMINAL_STATES = Object.freeze([...TERMINAL_STATES]);
export const TTORIA_ACTIVITY_SELECTOR_PRESENTERS = Object.freeze([...SELECTOR_PRESENTERS]);
